/**
 * Whether a horizontally-scrolled board has work hidden off either edge, and —
 * when columns run off the RIGHT — the nearest NON-EMPTY column out there to
 * name, so "an empty board with gaps" reads as "finished work, just over here".
 *
 * Kept a pure function (geometry in, decision out) so the "which column do we
 * point at" rule is unit-tested without a browser: with In Staging on and every
 * card in Done, the four empty columns fill the view and Done is off-screen —
 * the marker must skip the empties and name Done, and must say nothing when the
 * only thing off-screen is itself empty, or when nothing is off-screen at all.
 */
export interface BoardCol {
  /** The column's left edge within the scroll content (0 = the first column). */
  left: number;
  width: number;
  count: number;
  label: string;
  status: string;
}

export interface OffscreenCue {
  left: boolean;
  right: boolean;
  marker: { label: string; count: number; status: string; left: number } | null;
}

export function offscreenCue(cols: BoardCol[], scrollLeft: number, clientWidth: number): OffscreenCue {
  const contentRight = cols.reduce((m, c) => Math.max(m, c.left + c.width), 0);
  const viewRight = scrollLeft + clientWidth;
  const canLeft = scrollLeft > 1;
  const canRight = viewRight < contentRight - 1;
  let marker: OffscreenCue['marker'] = null;
  if (canRight) {
    // Columns are in left-to-right order, so the first non-empty one whose left
    // edge sits at (or just before) the viewport's right edge is the nearest
    // hidden work. A small margin lets a bare sliver still count as "hidden".
    const off = cols.find((c) => c.count > 0 && c.left >= viewRight - 32);
    if (off) marker = { label: off.label, count: off.count, status: off.status, left: off.left };
  }
  return { left: canLeft, right: canRight, marker };
}
